/**
 * 68.42: the planet table's view of the real match — what the side knows, as one scene both projections draw
 * (ui/planetTableDraw.ts; the globe drapes its ground layer, the flat map draws it straight). Pure: no canvas, no three.
 *
 * - **The frame:** azimuthal equidistant about the midpoint between the bases (the board's, else the teams' anchors),
 *   "north" toward the enemy's base; the map's unit a person (map distance is walking distance).
 * - **The objectives** as the side knows them (core/objectiveIntel.ts `sitesAsKnown`: with objectives known roughly, an
 *   unfound one at its circle's centre, dimmed), lettered as the HUD letters them; out of play (another sector's) dimmed.
 * - **The fronts:** a field from the held objectives (as known) and the bases; its zero is the battle line (the owner,
 *   DECISIONS 216: "squiggly lines of who holds what"). With territory on (`ModeConfig.territory`, 216 (4)), the territory
 *   field instead (core/territory.ts: each route vertex the side nearest it over the routes from what it holds): its zero
 *   the line between the sides, its contested band shaded (`contested`); each read blends the nearest vertex and its
 *   neighbours (`territorySampler`), so the line runs smooth across the vertices' cells.
 * - **The webs** (core/web.ts `webLinks`), the side's buildings (relays, FOBs, pads, beacons, guns), its vehicles, its
 *   ships and their runs (ui/dropshipMarks.ts), its standing calls (the strikes' circles), its plays.
 * - **Groups:** the side's efforts (§0.11 step D, `state.efforts`, GameConfig.efforts) — each main effort, hold and raid a
 *   group (its squads lead first, its axis from their middle to its objective: a point's letter, a structure, a place); the
 *   reserve effort on the base's tab. The other side's efforts are never drawn (the table shows only its picture). Squads
 *   in no effort, and every squad when the side has none, group by where their orders send them — an objective (its
 *   letter) or a place (40 people); squads with no order at the base are the reserve (on the base's tab); others group by
 *   where they stand, with no axis.
 * - **The picture:** the enemy as the team knows it (the table's beliefs), by age.
 * - **Routes:** the match's main routes as given (`opts.routes`: core/planetRoutes.ts `mainRoutes`, on this planet). With
 *   objectives known roughly, a run's ends inside the circle of an objective the side hasn't found are cut off at the circle
 *   (inside it the run homes in on the true place).
 * - **Roads** (GameConfig.roads) as given (`opts.roads`: core/roads.ts `roadLines`, on this planet), their ends cut the same way.
 */
import type { Effort, GameState, PlanetState, Vec3 } from '../types';
import { PHYS_DT, TEAMS } from '../constants';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { teamAnchors, activeSites } from '../core/capturePoints';
import { sitesAsKnown, knownObjectives } from '../core/objectiveIntel';
import { webLinks } from '../core/web';
import { territoryField, type TerritoryField } from '../core/territory';
import { planetRouteGraph } from '../core/planetRoutes';
import { nearestSurfaceVertex } from '../core/surface';
import { buildingMarkers, STRUCTURE_LABEL, type CommandTable, type FriendlyStatus, type SquadMarker } from '../core/commandTable';
import { dropshipMarks } from './dropshipMarks';
import { standingCalls } from './callMarks';
import { toMap, fromMap, type TopoFrame } from './topoMap';
import { VEHICLE_GLYPH, type IconId } from './commandIcons';
import { clockText, type M, type TableGroup, type TableScene, type TableZoom } from './planetTableDraw';
import type { ClimateCellId } from '../core/climate';
import type { FrontMark } from './frontMarks';
import type { MapWindow } from './planetTableMap';

/** A friendly's state as its marker's glyph (the table's own, rendering/commandOverlay.ts). */
const STATUS_GLYPH: Record<FriendlyStatus, IconId> = {
  'in contact': 'contact', moving: 'moving', holding: 'holding', scouting: 'scouting', crewing: 'crewing',
  withdrawing: 'withdrawing', hurt: 'hurt', down: 'hurt', free: 'holding', supporting: 'supporting',
};
const BUILDING_ICON: Partial<Record<string, IconId>> = { relay: 'relay', pad: 'pad', beacon: 'beacon', gun: 'gun' };
/** An effort's kind as its group's task and glyph (§0.11 step D; the reserve is the base's tab). */
const EFFORT_TASK: Record<Effort['kind'], string> = { main: 'take', hold: 'hold', raid: 'raid', reserve: 'wait' };
const EFFORT_GLYPH: Record<Effort['kind'], IconId> = { main: 'contact', hold: 'holding', raid: 'scouting', reserve: 'holding' };
/** An order's place within this many capture radii of an objective is that objective's. */
const SNAP_RADII = 3;
/** Places closer than this (people) are one group's. */
const GROUP_CELL = 40;
/** An unordered squad this near its base (people) is the reserve. */
const RESERVE_REACH = 120;

export interface PlanetTable {
  frame: TopoFrame;
  /** a person's height in world units */
  person: number;
  /** the bases' separation along the ground, people */
  separation: number;
  scene: TableScene;
  /** each group's squads (ids), by the group's key — a click on a plate selects them */
  groupSquads: Map<string, number[]>;
  /** what changed: equal keys draw the same (the globe's drape redraws only on a change) */
  key: string;
}

const norm = (v: Vec3): Vec3 => { const l = Math.hypot(v.x, v.y, v.z) || 1; return { x: v.x / l, y: v.y / l, z: v.z / l }; };
const crossV = (a: Vec3, b: Vec3): Vec3 => ({ x: a.y * b.z - a.z * b.y, y: a.z * b.x - a.x * b.z, z: a.x * b.y - a.y * b.x });
const dotV = (a: Vec3, b: Vec3) => a.x * b.x + a.y * b.y + a.z * b.z;
const dist = (a: M, b: M) => Math.hypot(a.x - b.x, a.y - b.y);

/** A territory field for one side as two values a route vertex: its side (+1 the viewer's, −1 the other's, 0 contested or no one's), smoothed once over the vertex's neighbours, and its contest (1 in the band). */
const territoryMemo = new WeakMap<TerritoryField, Map<number, { side: Float32Array; band: Float32Array }>>();
function territoryValues(planet: PlanetState, field: TerritoryField, team: number): { side: Float32Array; band: Float32Array } {
  let byTeam = territoryMemo.get(field);
  if (!byTeam) { byTeam = new Map(); territoryMemo.set(field, byTeam); }
  const hit = byTeam.get(team);
  if (hit) return hit;
  const g = planetRouteGraph(planet), n = g.count, side0 = new Float32Array(n), band0 = new Float32Array(n);
  for (let v = 0; v < n; v++) { const o = field.owner[v]; side0[v] = o < 0 ? 0 : o === team ? 1 : -1; band0[v] = o < 0 ? 1 : 0; }
  const smooth = (a: Float32Array) => {
    const out = new Float32Array(n);
    for (let v = 0; v < n; v++) { let sum = a[v], cnt = 1; for (let k = g.start[v]; k < g.start[v + 1]; k++) { sum += a[g.nbr[k]]; cnt++; } out[v] = sum / cnt; }
    return out;
  };
  const out = { side: smooth(side0), band: band0 };   // (the band is a vertex or two wide: smoothed it would fade out)
  byTeam.set(team, out);
  return out;
}
/**
 * The territory's two fields over the map (`control`, `contested`), read at a place by blending the nearest vertex and its
 * neighbours by nearness (so the line between the sides runs smooth, not along the vertices' cells). The nearest vertex is
 * walked to over the graph from the last one read (the drawings read in scan order, so a step or two; the binned search
 * cost 1 µs a read, 22 ms for the drape's tint); `contested` at the place just read reuses that read.
 */
function territorySampler(planet: PlanetState, frame: TopoFrame, vals: { side: Float32Array; band: Float32Array }): { control: (m: M) => number; contested: (m: M) => number } {
  const g = planetRouteGraph(planet), D = g.dirs;
  let hint = -1, lx = NaN, ly = NaN, lband = 0;
  const read = (m: M): number => {   // (no closures inside: a read allocates only its direction)
    const d = fromMap(frame, m.x, m.y), l = Math.hypot(d.x, d.y, d.z) || 1, dx = d.x / l, dy = d.y / l, dz = d.z / l;
    let v0 = hint < 0 ? g.nodeOf[nearestSurfaceVertex(planet, d)] : hint, best = D[3 * v0] * dx + D[3 * v0 + 1] * dy + D[3 * v0 + 2] * dz;
    for (let moved = true, steps = 0; moved && steps < 64; steps++) {
      moved = false;
      for (let k = g.start[v0], e = g.start[v0 + 1]; k < e; k++) { const w = g.nbr[k], c = D[3 * w] * dx + D[3 * w + 1] * dy + D[3 * w + 2] * dz; if (c > best) { best = c; v0 = w; moved = true; } }
    }
    if (best < 0.5) { v0 = g.nodeOf[nearestSurfaceVertex(planet, d)]; best = D[3 * v0] * dx + D[3 * v0 + 1] * dy + D[3 * v0 + 2] * dz; }   // (a jump across the planet: search again)
    hint = v0;
    let w = 1 / (1e-9 + 1 - best), side = w * vals.side[v0], band = w * vals.band[v0], wsum = w;
    for (let k = g.start[v0], e = g.start[v0 + 1]; k < e; k++) {
      const u = g.nbr[k];
      w = 1 / (1e-9 + 1 - (D[3 * u] * dx + D[3 * u + 1] * dy + D[3 * u + 2] * dz));
      side += w * vals.side[u]; band += w * vals.band[u]; wsum += w;
    }
    lx = m.x; ly = m.y; lband = band / wsum;
    return side / wsum;
  };
  return { control: read, contested: (m: M) => { if (m.x !== lx || m.y !== ly) read(m); return lband; } };
}

/** The frame about the midpoint of `own` and `them` (unit directions), north toward `them`; radius in people. */
export function tableFrame(planetIndex: number, own: Vec3, them: Vec3, radiusPeople: number): TopoFrame {
  const a = norm(own), b = norm(them);
  let c = { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
  if (Math.hypot(c.x, c.y, c.z) < 1e-6) {   // antipodal (a board of ½): any point on the great circle between, a fixed choice
    const ref = Math.abs(a.y) < 0.9 ? { x: 0, y: 1, z: 0 } : { x: 1, y: 0, z: 0 };
    c = crossV(a, ref);
  }
  const centre = norm(c);
  const toward = { x: b.x - centre.x * dotV(b, centre), y: b.y - centre.y * dotV(b, centre), z: b.z - centre.z * dotV(b, centre) };
  const north = Math.hypot(toward.x, toward.y, toward.z) > 1e-9 ? norm(toward) : norm(crossV(centre, crossV(a, centre)));
  return { planetIndex, centre, north, east: crossV(north, centre), radius: radiusPeople, extent: radiusPeople * Math.PI * 0.5 };
}

/** The view's map window at a zoom: the planet's hemisphere, the theatre between the bases, or a sector about `focus`. */
export function tableWindow(t: Pick<PlanetTable, 'frame' | 'separation' | 'scene'>, zoom: TableZoom, focus: M = { x: 0, y: 0 }): MapWindow {
  if (zoom === 'planet') return { x: 0, y: 0, extent: Math.PI * t.frame.radius * 0.56 };
  if (zoom === 'sector') return { x: focus.x, y: focus.y, extent: 150 };
  const across = Math.max(0, ...t.scene.objectives.map(o => Math.abs(o.at.x)));
  return { x: 0, y: 0, extent: Math.min(Math.PI * t.frame.radius * 0.9, Math.max((t.separation / 2) * 1.18, across * 1.3, 200)) };
}

/** The side's table at `tick`: the frame, the scene, the groups. Null when the side has no world to draw (no points, no board). */
export function planetTableOf(state: GameState, team: number, tick: number, table: CommandTable, opts: { routes?: Array<{ line: Vec3[]; main: boolean; planetIndex?: number }>; roads?: Array<{ line: Vec3[]; planetIndex: number; trail?: boolean }>; sheet?: { planetIndex: number; chains: Array<{ line: Vec3[]; order: number }>; lakes: Array<{ at: Vec3[]; r: number; playa?: boolean }>; candidates: Array<{ at: Vec3; kind: 'pass' | 'ford' | 'crossing' }>; climate?: { at: Vec3[]; cell: ClimateCellId[]; r: number }; woods?: { at: Vec3[]; density: number[]; r: number } }; fronts?: Array<FrontMark & { planetIndex: number }> } = {}): PlanetTable | null {
  const sites = state.modeState.points?.sites ?? [];
  const boardBases = state.board?.bases ?? [];
  const planetIndex = boardBases[0]?.planetIndex ?? sites[0]?.planetIndex ?? state.players.find(p => p.teamIndex === team)?.planetIndex;
  if (planetIndex === undefined) return null;
  const planet = state.planets[planetIndex];
  if (!planet) return null;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, R = planet.radius / person;
  // the bases: the board's, else the teams' anchors
  const anchors = teamAnchors(state, planetIndex);
  const baseOf = (t: number): Vec3 | undefined => boardBases.find(b => b.team === t)?.dir ?? anchors.get(t);
  const otherTeam = [...new Set([...boardBases.map(b => b.team), ...anchors.keys()])].find(t => t !== team);
  const ownBase = baseOf(team), theirBase = otherTeam === undefined ? undefined : baseOf(otherTeam);
  if (!ownBase || !theirBase) return null;
  const frame = tableFrame(planetIndex, ownBase, theirBase, R);
  const at = (d: Vec3): M => toMap(frame, d);
  const own = TEAMS[team % TEAMS.length].primary, them = otherTeam === undefined ? '#e05a4f' : TEAMS[otherTeam % TEAMS.length].primary;
  const colour = (t: number | null | undefined) => t === null || t === undefined ? null : TEAMS[t % TEAMS.length].primary;
  const separation = Math.acos(Math.max(-1, Math.min(1, dotV(norm(ownBase), norm(theirBase))))) * R;
  const homeAt = at(ownBase), theirAt = at(theirBase);

  // the objectives, as the side knows them
  const known = sitesAsKnown(state, team, sites, tick);
  const rough = state.config.roughStart ? knownObjectives(state, team, tick) : null;
  const exact = rough ? new Map(rough.map(k => [k.siteId, k.resolved] as const)) : null;
  const active = new Set(activeSites(state).map(s => s.id));
  const ring = (state.config.modeConfig.captureRadius ?? ps.captureRadius) / person;
  const objectives = known.map((s, i) => ({
    id: s.id, at: at(s.surfaceDir), letter: String.fromCharCode(65 + i), holder: colour(s.owner), owner: s.owner,
    ...(s.capturer !== null && s.progress > 0 ? { taking: { color: colour(s.capturer)!, progress: s.progress } } : {}),
    dim: !active.has(s.id) || exact?.get(s.id) === false, ring,
  }));

  // a route's run inside the circle of an objective the side hasn't found (rough objectives) homes in on where it is: its ends cut there
  const circles = rough ? rough.filter(k => !k.resolved && k.planetIndex === planetIndex).map(k => ({ at: at(k.surfaceDir), r: k.radius / person })) : [];
  const inCircle = (m: M) => circles.some(c => dist(c.at, m) <= c.r);
  const trimmed = (line: M[]): M[] => {
    let a = 0, b = line.length;
    while (b > a && inCircle(line[b - 1])) b--;
    while (a < b && inCircle(line[a])) a++;
    return line.slice(a, b);
  };

  // the fronts: with territory on (216 (4)), the territory field — its line between the sides, its contested band; else the held objectives (a capture under way weighs less) and the bases
  const field = state.config.modeConfig.territory ? territoryField(state, { team, tick }) : null;   // (as the side knows it: unfound objectives seed nothing)
  const territory = field && field.planetIndex === planetIndex ? territoryValues(planet, field, team) : null;
  const D = Math.max(separation, 200), sigma = D * 0.13, sb = D * 0.25;
  const held = objectives.filter(o => o.owner !== null).map(o => ({ at: o.at, w: (o.owner === team ? 1 : -1) * (o.taking ? 0.35 : 1) }));
  const sampler = territory ? territorySampler(planet, frame, territory) : null;
  const control = sampler ? sampler.control : (m: M): number => {
    let f = 2.2 * Math.exp(-(dist(m, homeAt) ** 2) / (2 * sb * sb)) - 2.2 * Math.exp(-(dist(m, theirAt) ** 2) / (2 * sb * sb));
    for (const h of held) f += h.w * Math.exp(-(dist(m, h.at) ** 2) / (2 * sigma * sigma));
    return f;
  };
  const contested = sampler?.contested;

  // groups: the side's efforts (§0.11 step D), then the squads in none by where their orders send them
  const mine = table.squads.filter(q => q.team === team && q.centre && q.alive > 0);
  const snap = (d: Vec3): { key: string; at: M; name: string } => {
    const m = at(d), near = objectives.map(o => ({ o, d: dist(o.at, m) })).sort((a, b) => a.d - b.d)[0];
    if (near && near.d <= ring * SNAP_RADII) return { key: `o${near.o.id}`, at: near.o.at, name: near.o.letter };
    return { key: `p${Math.round(m.x / GROUP_CELL)},${Math.round(m.y / GROUP_CELL)}`, at: m, name: '' };
  };
  type Bin = { squads: SquadMarker[]; target?: { at: M; name: string }; here?: string; effort?: Effort };
  const bins = new Map<string, Bin>();
  let reserve = 0, reserveAlive = 0;
  const byId = new Map(mine.map(q => [q.id, q]));
  const efforts = (state.efforts ?? []).filter(e => e.team === team);   // (never the other side's)
  const inEffort = new Set(efforts.flatMap(e => e.squads));
  for (const e of efforts) {
    const squads = e.squads.map(id => byId.get(id)).filter((q): q is SquadMarker => !!q);   // (lead first, as the effort lists them)
    if (!squads.length) continue;
    if (e.kind === 'reserve' || !e.objective) { for (const q of squads) { reserve++; reserveAlive += q.alive; } continue; }
    const o = e.objective;
    let target: { at: M; name: string } | undefined;
    if ('pointId' in o) { const k = objectives.find(x => x.id === o.pointId); if (k) target = { at: k.at, name: k.letter }; }
    else if ('structureId' in o) { const st = state.structures.find(x => x.id === o.structureId); if (st && st.planetIndex === planetIndex) target = { at: at(st.surfaceDir), name: STRUCTURE_LABEL[st.type] ?? st.type }; }
    else if (o.planetIndex === planetIndex) { const t = snap(o.surfaceDir); target = { at: t.at, name: t.name }; }
    bins.set(`e${e.id}`, { squads, target, effort: e });
  }
  for (const q of mine) {
    if (inEffort.has(q.id)) continue;
    const c = at(q.centre!);
    if (!q.target) {
      if (dist(c, homeAt) <= RESERVE_REACH) { reserve++; reserveAlive += q.alive; continue; }
      // no order: grouped by where it stands (an objective, or the place), no axis
      const h = snap(q.centre!), bin = bins.get(`u${h.key}`) ?? { squads: [], here: h.name };
      bin.squads.push(q); bins.set(`u${h.key}`, bin);
      continue;
    }
    const t = snap(q.target.dir), bin = bins.get(t.key) ?? { squads: [], target: { at: t.at, name: t.name } };
    bin.squads.push(q); bins.set(t.key, bin);
  }
  const ordinal = (n: number) => `${n}${n % 10 === 1 && n % 100 !== 11 ? 'st' : n % 10 === 2 && n % 100 !== 12 ? 'nd' : n % 10 === 3 && n % 100 !== 13 ? 'rd' : 'th'}`;
  // the frontmost first (toward their base)
  const ordered = [...bins.entries()].sort((a, b) => (b[1].target?.at.y ?? -Infinity) - (a[1].target?.at.y ?? -Infinity) || a[0].localeCompare(b[0]));
  const groupSquads = new Map<string, number[]>();
  const mains = ordered.filter(([, b]) => b.effort?.kind === 'main').length;
  let nth = 0, nthMain = 0;
  const groups: TableGroup[] = ordered.map(([, bin], i) => {
    const e = bin.effort;
    const lead = e ? bin.squads[0] : [...bin.squads].sort((a, b) => b.alive - a.alive || a.id - b.id)[0];
    const middle = bin.squads.map(q => at(q.centre!)).reduce((s, m, _, all) => ({ x: s.x + m.x / all.length, y: s.y + m.y / all.length }), { x: 0, y: 0 });
    const key = String(i + 1), verb = lead.order.split(' ')[0] || 'free';
    groupSquads.set(key, bin.squads.map(q => q.id));
    const alive = bin.squads.reduce((s, q) => s + q.alive, 0), total = bin.squads.reduce((s, q) => s + q.total, 0);
    const name = e ? (e.kind === 'main' ? (mains > 1 ? `${ordinal(++nthMain)} main effort` : 'main effort') : e.kind === 'hold' ? 'hold' : 'raid')
      : bin.squads.length > 1 ? `${ordinal(++nth)} group` : lead.label;
    const task = e ? EFFORT_TASK[e.kind] : bin.target ? verb : bin.here ? 'at' : verb;
    return {
      key, name, task, targetName: bin.target?.name ?? bin.here ?? '', glyph: e ? EFFORT_GLYPH[e.kind] : STATUS_GLYPH[lead.status],
      ...(bin.target && dist(middle, bin.target.at) > GROUP_CELL ? { from: middle, target: bin.target.at } : {}),
      squads: bin.squads.map(q => ({ at: at(q.centre!), n: q.label.split(' ')[0], state: STATUS_GLYPH[q.status], words: q.order, strength: `${q.alive}/${q.total}` })),
      strength: `${alive}/${total}`,
      dashed: e ? e.kind === 'raid' : lead.glyph === 'scout' || lead.status === 'scouting',
      faint: e ? e.kind === 'hold' : lead.status === 'holding',
    };
  });
  const effortOf = new Map(ordered.map(([, b], i) => [String(i + 1), b.effort] as const));

  const ships = dropshipMarks(state, team).filter(s => s.planetIndex === planetIndex).map(s => ({
    at: at(s.dir), ...(s.to ? { to: at(s.to) } : {}),
    label: s.state === 'parked' ? `ship · on its pad · ${s.riders} aboard` : s.state === 'landed' ? 'ship · down' : `ship · ${s.riders} aboard`,
  }));
  const strikes = standingCalls(state, team, tick).filter(c => c.sub === 'airstrike' && c.circles.length && c.planetIndex === planetIndex).map(c => ({
    at: at(c.circles[0].at), r1: c.circles[0].people, r2: c.circles[1]?.people ?? c.circles[0].people * 1.6, label: `strike · ${clockText((c as { seconds: number }).seconds)}`,
  }));
  const buildings = buildingMarkers(state, team).filter(b => b.planetIndex === planetIndex && !b.ruined && b.team === team && (BUILDING_ICON[b.kind] || b.label === 'FOB'))
    .map(b => ({ at: at(b.dir), icon: (b.label === 'FOB' ? 'fob' : BUILDING_ICON[b.kind]) as IconId, color: own, ...(b.label === 'FOB' ? { size: 20 } : {}) }));
  const scene: TableScene = {
    own, them, enemy: '#e05a4f',
    bases: [
      { at: homeAt, mine: true, tab: reserve ? `Base · reserve ${reserve} squad${reserve === 1 ? '' : 's'} (${reserveAlive})` : 'Base' },
      { at: theirAt, mine: false, tab: 'their base' },
    ],
    objectives: objectives.map(({ id: _id, owner: _owner, ...o }) => o),
    roads: (opts.roads ?? []).filter(r => r.planetIndex === planetIndex && !r.trail).map(r => circles.length ? trimmed(r.line.map(at)) : r.line.map(at)).filter(l => l.length >= 2),
    trails: (opts.roads ?? []).filter(r => r.planetIndex === planetIndex && r.trail).map(r => circles.length ? trimmed(r.line.map(at)) : r.line.map(at)).filter(l => l.length >= 2),   // (74.5: the trails, thin)
    ...(opts.fronts && opts.fronts.some(f => f.planetIndex === planetIndex) ? { fronts: opts.fronts.filter(f => f.planetIndex === planetIndex).map(f => ({ at: at(f.centre), to: at(f.to), r: f.radiusPeople, kind: f.kind, ease: f.ease })) } : {}),   // 74.11 (b)
    ...(opts.sheet && opts.sheet.planetIndex === planetIndex ? { sheet: {   // 74.3 step 0
      chains: opts.sheet.chains.map(c => ({ line: c.line.map(at), order: c.order })).filter(c => c.line.length >= 2),
      lakes: opts.sheet.lakes.map(l => ({ at: l.at.map(at), r: l.r, ...(l.playa ? { playa: true } : {}) })),
      candidates: opts.sheet.candidates.map(c => ({ at: at(c.at), kind: c.kind })),
      ...(opts.sheet.climate ? { climate: { at: opts.sheet.climate.at.map(at), cell: opts.sheet.climate.cell, r: opts.sheet.climate.r } } : {}),   // 74.12 step 1
      ...(opts.sheet.woods ? { woods: { at: opts.sheet.woods.at.map(at), density: opts.sheet.woods.density, r: opts.sheet.woods.r } } : {}),   // 393 (3) step 1
    } } : {}),
    routes: (opts.routes ?? []).filter(r => (r.planetIndex ?? planetIndex) === planetIndex).map(r => ({ line: circles.length ? trimmed(r.line.map(at)) : r.line.map(at), main: r.main })).filter(r => r.line.length >= 2),
    links: webLinks(state, team).filter(l => l.planetIndex === planetIndex).map(l => ({ a: at(l.a), b: at(l.b), live: l.live })),
    enemyLinks: [],
    buildings,
    settlements: [],
    groups,
    ships,
    vehicles: table.transports.filter(v => v.team === team && !v.enemy && v.planetIndex === planetIndex).map(v => ({ at: at(v.dir), icon: VEHICLE_GLYPH[v.kind] as IconId })),
    marks: table.enemy.filter(b => b.planetIndex === planetIndex).map(b => ({ at: at(b.surfaceDir), kind: b.unitId === undefined ? 'unknown' as const : 'infantry' as const, age: Math.max(0, (tick - b.tick) * PHYS_DT) })),
    plays: table.plays.filter(p => p.objective).map(p => ({ a: at(p.support.dir), b: at(p.objective!.dir), label: `play ${p.id}` })),
    strikes,
    control,
    ...(contested ? { contested } : {}),
  };
  // the change key: what the drape shows, coarsely (ten people, a 24th of a capture, the marks by their age's band)
  const r10 = (m: M) => `${Math.round(m.x / 10)},${Math.round(m.y / 10)}`;
  const key = [
    objectives.map(o => `${o.letter}${o.owner ?? '-'}${o.taking ? Math.round(o.taking.progress * 24) : ''}${o.dim ? 'd' : ''}${r10(o.at)}`).join(''),
    groups.map(g => `${g.key}${effortOf.get(g.key) ? `e${effortOf.get(g.key)!.id}${effortOf.get(g.key)!.kind}` : ''}:${g.from ? r10(g.from) : ''}>${g.target ? r10(g.target) : ''}${g.faint ? 'f' : ''}${g.dashed ? 'd' : ''}`).join(';'),
    scene.bases[0].tab,
    scene.links.map(l => `${r10(l.a)}${r10(l.b)}${l.live ? 1 : 0}`).join(''),
    ships.map(s => `${r10(s.at)}${s.to ? r10(s.to) : ''}`).join(''),
    strikes.map(s => r10(s.at)).join(''),
    scene.plays.map(p => r10(p.a)).join(''),
    scene.routes.length,
    scene.roads.length,
    field ? `t${Object.entries(field.share).map(([t, v]) => `${t}:${Math.round(v * 1000)}`).join(',')}c${Math.round(field.contested * 1000)}` : '',
  ].join('|');
  return { frame, person, separation, scene, groupSquads, key };
}
